import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DropdownItem,
  Label,
  CustomInput,
  FormFeedback,
  FormGroup,
  Input,
  UncontrolledPopover,
  PopoverBody,
} from 'reactstrap';
import cx from 'classnames';

import { getCurrOrg } from '../../redux/orgs/selectors';
import { loadTreatments, RESET_LOAD_TREATMENTS } from '../../redux/search/actions';
import { action } from '../../redux/helpers';
import { SearchDropdown } from './SearchDropdown';
import { getTreatments } from '../../redux/search/selectors';
import treatmentTypes from '../../utils/data/treatments';

import styles from './TreatmentInput.module.scss';
import text from '../../styles/text.module.scss';

// eslint-disable-next-line no-unused-vars
export const TreatmentInput = React.forwardRef((props, ref) => {
  const {
    value: treatment,
    onChange,
    error,
    className,
    disabled,
  } = props;
  const [selectedType, setSelectedType] = useState(_.get(treatment, 'treatment_type.category'));
  const dispatch = useDispatch();
  const currOrg = useSelector(getCurrOrg);
  const treatments = useSelector(getTreatments);
  const currPage = useSelector((s) => s.search.currTreatmentsPage);
  const hasMore = useSelector((s) => s.search.hasMoreTreatments);
  const loading = useSelector((s) => s.search.loadTreatmentsPending);

  const resetTreatments = useCallback(() => {
    dispatch(action(RESET_LOAD_TREATMENTS));
  }, [currOrg, selectedType]);

  const loadMoreTreatments = useCallback(({ query }) => {
    dispatch(loadTreatments(currPage + 1, query, selectedType));
  }, [currPage, selectedType]);

  return (
    <div className={className}>
      <FormGroup>
        <Label>Treatment Type *</Label>
        <CustomInput
          id="treatment-type"
          type="select"
          value={selectedType}
          className="form-control"
          onChange={(e) => (
            setSelectedType(e.target.value),
            onChange({ ...treatment, treatment_type: '' })
          )}
          disabled={disabled}
        >
          { _.map(
            treatmentTypes,
            (t, index) => (
              <option
                key={`type-${index}`}
                value={t.value}
              >
                {t.text}
              </option>
            ),
          )}
        </CustomInput>
      </FormGroup>
      <FormGroup>
        <Label>Treatment *</Label>
        <FontAwesomeIcon
          className="ml-2"
          icon="info-circle"
          id="legend-popover"
        />
        <UncontrolledPopover
          placement="right"
          target="legend-popover"
          trigger="hover"
        >
          <PopoverBody>
            <FontAwesomeIcon icon="fish" className="mr-2" />
            = Food Fish Approved
          </PopoverBody>
        </UncontrolledPopover>
        <SearchDropdown
          className={cx({'is-invalid': error})}
          placeholder="Select treatment"
          displayText={_.get(treatment, 'treatment_type.name')}
          firstLoad={resetTreatments}
          loadMore={loadMoreTreatments}
          hasMore={hasMore}
          loading={loading}
          disabled={!selectedType || disabled}
        >
          { !loading && _.get(treatments, 'length', 0) == 0 && (
            <div className="text-center">
              No Treatments
            </div>
          )}
          { _.map(
            treatments,
            (t) => (
              <DropdownItem
                key={`treatment-${t.id}`}
                onClick={() => onChange({ ...treatment, treatment_type: t })}
                className={cx(
                  { 'selected': t.id == _.get(treatment, 'treatment_type.id') },
                  styles.itemContainer,
                )}
              >
                <div className={cx(text.ellipsize, styles.itemBox)}>
                  { t.name }
                </div>
                {t.food_fish_approved && (
                  <FontAwesomeIcon
                    icon="fish"
                    className="ml-2"
                  />
                )}
              </DropdownItem>
            ),
          ) }
        </SearchDropdown>
        <FormFeedback role="alert">
          { _.get(error, 'treatment_type') }
        </FormFeedback>
      </FormGroup>
      <FormGroup>
        <Label>Dose</Label>
        <Input
          type="textarea"
          value={treatment.description || ''}
          onChange={(e) => onChange({ ...treatment, description: e.target.value })}
          autoComplete="off"
          placeholder="Description of treatment"
          invalid={_.has(error, 'description')}
          disabled={disabled}
        />
        <FormFeedback role="alert">
          { _.get(error, 'description') }
        </FormFeedback>
      </FormGroup>
    </div>
  );
});
TreatmentInput.displayName = 'TreatmentInput';
TreatmentInput.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
};
